/**
 * TL-064 First login: sends a new institute's owner to the setup wizard.
 * Only when the tenant isn't set up, the owner hasn't put setup away
 * (`dismissed`), and only once per browser session, so they can leave the
 * wizard and work without being pulled back. Renders nothing.
 */

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useSetup } from "@/lib/api/tenantSettings";
import { useTenant } from "@/lib/tenant";
import { ROUTES } from "@/types/routes";

import { scopedKey } from "@/lib/tenant/basePath";
const SEEN_KEY = scopedKey("tenant.setup.prompted");

function seen(): boolean {
  try {
    return window.sessionStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function SetupGate() {
  const { user } = useAuth();
  const tenant = useTenant();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const wanted = Boolean(user?.isOwner) && !tenant.setupComplete && !tenant.isInternal && !seen();
  const setup = useSetup(wanted);

  useEffect(() => {
    if (!wanted || !setup.data || pathname === ROUTES.SETUP) return;
    try {
      window.sessionStorage.setItem(SEEN_KEY, "1");
    } catch {
      /* Without storage the prompt simply shows once per page load. */
    }
    if (!setup.data.dismissed && !setup.data.complete) navigate(ROUTES.SETUP, { replace: true });
  }, [wanted, setup.data, pathname, navigate]);

  return null;
}
